import type { ListBuilderConfiguration } from '../types';

/**
 * Ecommerce Users list configuration
 * Converted from JSON to TypeScript for type safety
 */
export const ECOMMERCE_USER_LIST_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-ecommerce-users-all',
			cacheTTL: 600000,
			name: 'All Ecommerce Users',
			type: 'default',
			active: true,
			defaultSortId: 'firstName-asc',
			filterRules: {
				ecommerceUserStatus: {
					defaultValue: [{ id: 'All', label: 'All' }],
				},
				ecommerceUserClientSalesRep: {
					defaultValue: [{ id: 'ME', label: 'Me' }],
					applyDefaultIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					lockedIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
					hiddenIf: {
						type: 'group',
						joiner: 'AND',
						negated: false,
						children: [
							{ type: 'rule', action: 'clients:detail:any', negated: true },
						],
					},
				},
			},
			columns: [
				{
					id: 'name',
					headers: [
						{
							id: 'name-header',
							text: 'Name',
						},
					],
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'public_user',
							},
							dataConfig: {
								type: 'template',
								template: '{firstName} {lastName}',
								templateFields: [],
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'medium',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'email',
					headers: [
						{
							id: 'email-header',
							text: 'Email',
						},
					],
					fields: [
						{
							id: 'email-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'email',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: true,
					sortFields: [
						{
							objectTable: 'public_user',
							fieldName: 'email',
							direction: 'ASC',
						},
					],
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'roles',
					headers: [
						{
							id: 'roles-header',
							text: 'Roles',
						},
					],
					fields: [
						{
							id: 'roles-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'roleNames',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
								valueMapping: [
									{
										value: 'Ecommerce Customer',
										displayText: 'Buyer',
									},
									{
										value: 'Ecommerce Admin',
										displayText: 'Admin',
									},
								],
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
				{
					id: 'clients',
					headers: [
						{
							id: 'clients-header',
							text: 'Clients',
						},
					],
					fields: [
						{
							id: 'clients-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: 'clientNames',
									objectTable: 'public_user',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: {
									type: 'default',
								},
							},
							displayStyle: 'text',
						},
					],
					sortable: false,
					widthConfig: {
						mode: 'flexible',
						preset: 'large',
					},
					verticalAlignment: 'top',
				},
			],
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [10, 25, 50, 100],
	},
	filters: {
		activeFilters: [
			'ecommerceUserSearch',
			'ecommerceUserStatus',
			'ecommerceUserClientSalesRep',
			'ecommerceUserCompany',
		],
		defaultFilters: {},
	},
	tableSorts: [
		{
			id: 'firstName-asc',
			name: 'First Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'firstName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'firstName-desc',
			name: 'First Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'firstName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'lastName-asc',
			name: 'Last Name (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'lastName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'lastName-desc',
			name: 'Last Name (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'lastName',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'email-asc',
			name: 'Email (A-Z)',
			fields: [
				{
					direction: 'ASC',
					fieldName: 'email',
					objectTable: 'public_user',
				},
			],
		},
		{
			id: 'email-desc',
			name: 'Email (Z-A)',
			fields: [
				{
					direction: 'DESC',
					fieldName: 'email',
					objectTable: 'public_user',
				},
			],
		},
	],
	objects: [
		{
			baseUrl: '/ecommerce-users/:id',
			objectLabel: 'Ecommerce Users',
			objectTable: 'public_user',
			selectedFields: [
				{
					fieldName: 'id',
					fieldLabel: 'ID',
				},
				{
					fieldName: 'email',
					fieldLabel: 'Email',
				},
				{
					fieldName: 'firstName',
					fieldLabel: 'First Name',
				},
				{
					fieldName: 'lastName',
					fieldLabel: 'Last Name',
				},
				{
					fieldName: 'roleNames',
					fieldLabel: 'Roles',
				},
				{
					fieldName: 'clientNames',
					fieldLabel: 'Clients',
				},
			],
		},
	],
};
